import { FC, useMemo } from "react";
import {
  Box,
  Link as LinkBase,
  Typography,
  Stack,
} from "@mui/material";
import { CoachPetAvatar } from "components/VetStore/CoachPetAvatar";
import { OpenInNew } from "@mui/icons-material";
import { Modal } from "components/Modal";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import {
  getFeedbackDetailPath,
  isFeedbackExpired,
  shouldHideFeedback,
} from "utils/feedback";
import { formatTime } from "pages/Analytics/utils";
import CustomTable from "components/Table/CustomTable";
import dayjs from "dayjs";
import { useAuthUser } from "hooks/useAuthUser";
import { useAppSelector } from "hooks/useAppSelector";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";

type FeedbackDisplayModalProps = {
  open: boolean;
  onClose: () => void;
  selectedStatusLabel: string;
  filteredData: FeedbackType[];
  displayStudentName?: boolean;
  userRole?: string;
  organizationId?: string;
};

// Map selected label to status for filtering and include special handling for expired
const mapLabelToStatus = (
  selectedStatusLabel: string,
): FeedbackStatus | null => {
  const normalized = (selectedStatusLabel || "").toUpperCase();
  if (normalized === "ACCEPTED") return FeedbackStatus.DONE;
  if (normalized === "PENDING") return FeedbackStatus.PENDING;
  if (normalized === "DECLINED" || normalized === "REJECTED")
    return FeedbackStatus.DECLINED;
  if (normalized === "EXPIRED") return FeedbackStatus.EXPIRED;
  return null;
};

// Calculate response time in hours and format it, "-" if not available
const getResponseTime = (row: FeedbackType) => {
  if (!row.reviewed_at || !row.created_at) return "-";
  const responseTimeInHours =
    (new Date(row.reviewed_at).getTime() - new Date(row.created_at).getTime()) /
    (1000 * 60 * 60);
  return formatTime(responseTimeInHours);
};

export const FeedbackDisplayModal: FC<FeedbackDisplayModalProps> = ({
  open,
  onClose,
  selectedStatusLabel,
  filteredData,
  displayStudentName = true,
  userRole,
  organizationId,
}) => {
  const { role: authRole } = useAuthUser();
  const selectedOrganization = useAppSelector(organizationSelectedSelector);
  const linkRole = userRole ?? authRole;
  const linkOrganizationId = organizationId ?? selectedOrganization?.org_id;

  const statusToFilter = useMemo(
    () => mapLabelToStatus(selectedStatusLabel),
    [selectedStatusLabel],
  );

  // Filter rows by selected status. Special handling for PENDING (exclude expired) and EXPIRED
  const rows = useMemo(() => {
    return (filteredData || []).filter((f) => {
      if (shouldHideFeedback(f)) return false;
      if (statusToFilter === null) return true;
      if (statusToFilter === FeedbackStatus.EXPIRED) {
        return f.status === FeedbackStatus.PENDING && isFeedbackExpired(f);
      }
      if (statusToFilter === FeedbackStatus.PENDING) {
        return f.status === FeedbackStatus.PENDING && !isFeedbackExpired(f);
      }
      return f.status === statusToFilter;
    });
  }, [filteredData, statusToFilter]);

  const avgResponseTimeHours = useMemo(() => {
    const reviewed = rows.filter((r) => r.reviewed_at && r.created_at);
    if (reviewed.length === 0) return 0;
    const sumHours = reviewed.reduce((acc, r) => {
      const createdAt = new Date(r.created_at || "");
      const reviewedAt = new Date(r.reviewed_at || "");
      return (
        acc + (reviewedAt.getTime() - createdAt.getTime()) / (1000 * 60 * 60)
      );
    }, 0);
    return sumHours / reviewed.length;
  }, [rows]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        selectedStatusLabel !== ""
          ? `Feedback List for ${selectedStatusLabel}`
          : "Feedback List"
      }
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      size="large"
    >
      <Box>
        <Box mb={2}>
          <Typography variant="body1" gutterBottom>
            <strong>Average response time:</strong>{" "}
            {formatTime(avgResponseTimeHours)}
          </Typography>
          <Typography variant="body1">
            <strong>Total feedback:</strong> {rows.length}
          </Typography>
        </Box>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No data to display
          </Typography>
        ) : (
          <CustomTable
            entities={rows}
            fieldToFilter={(row: FeedbackType, text: string) => {
              const filter = (text || "").toLowerCase();
              return (
                (displayStudentName &&
                  row.student?.name?.toLowerCase().includes(filter)) ||
                row.coach?.name?.toLowerCase().includes(filter)
              );
            }}
            columns={[
              {
                id: "coach",
                label: "Coach",
                value: (row: FeedbackType) => (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CoachPetAvatar
                      pictureUrl={row.coach?.picture_url}
                      name={row.coach?.name}
                      equippedStoreItems={row.coach?.equipped_store_items}
                      avatarSize={28}
                    />
                    <Typography variant="body2">{row.coach?.name}</Typography>
                  </Stack>
                ),
              },
              ...(displayStudentName
                ? [
                    {
                      id: "student",
                      label: "Student",
                      value: (row: FeedbackType) => (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CoachPetAvatar
                            pictureUrl={row.student?.picture_url}
                            name={row.student?.name}
                            equippedStoreItems={
                              row.student?.equipped_store_items
                            }
                            avatarSize={28}
                          />
                          <Typography variant="body2">
                            {row.student?.name}
                          </Typography>
                        </Stack>
                      ),
                    } as any,
                  ]
                : []),
              {
                id: "responseTime",
                label: "Response Time",
                value: (row: FeedbackType) => (
                  <Typography variant="body2">
                    {getResponseTime(row)}
                  </Typography>
                ),
              },
              {
                id: "observation_date",
                label: "Date",
                value: (row: FeedbackType) => (
                  <Typography variant="body2">
                    {row.observation_date
                      ? dayjs(row.observation_date).format("MMM D, YYYY")
                      : "-"}
                  </Typography>
                ),
              },
              {
                id: "openFeedback",
                label: "",
                value: (row: FeedbackType) => (
                  <Box display="flex" justifyContent="flex-end">
                    <LinkBase
                      href={getFeedbackDetailPath(
                        row.feedback_id,
                        linkRole,
                        linkOrganizationId,
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNew />
                    </LinkBase>
                  </Box>
                ),
              },
            ]}
            isSelectable={false}
            idColumn="feedback_id"
            handleActionClick={() => {}}
            extraActionItems={[]}
            enableCardViewInSmallDevices={true}
            enableMenuInSmallDevices={false}
            renderCardContent={(row: FeedbackType) => (
              <Stack spacing={0.5}>
                {displayStudentName && (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CoachPetAvatar
                      pictureUrl={row.student?.picture_url}
                      name={row.student?.name}
                      equippedStoreItems={row.student?.equipped_store_items}
                      avatarSize={28}
                    />
                    <Typography>{row.student?.name}</Typography>
                  </Stack>
                )}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">
                    <strong>Coach:</strong>
                  </Typography>
                  <CoachPetAvatar
                    pictureUrl={row.coach?.picture_url}
                    name={row.coach?.name}
                    equippedStoreItems={row.coach?.equipped_store_items}
                    avatarSize={28}
                  />
                  <Typography variant="body2">{row.coach?.name}</Typography>
                </Stack>
                <Typography variant="body2">
                  <strong>Response Time:</strong> {getResponseTime(row)}
                </Typography>
                <Typography variant="body2">
                  <strong>Observation:</strong>{" "}
                  {row.observation_date
                    ? dayjs(row.observation_date).format("MMM D, YYYY")
                    : "-"}
                </Typography>
              </Stack>
            )}
          />
        )}
      </Box>
    </Modal>
  );
};
